import type {
  ButtonHTMLAttributes,
  CSSProperties,
  ReactNode,
} from "react";

import { cn } from "@/lib/utils";

type BaselineActionCardProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "children"
> & {
  eyebrow: string;
  label: string;
  description: string;
  metaLabel: string;
  icon: ReactNode;
  overlay?: ReactNode;
  iconStyle?: CSSProperties;
  metaStyle?: CSSProperties;
  descriptionStyle?: CSSProperties;
  metaButtonProps?: Omit<
    ButtonHTMLAttributes<HTMLButtonElement>,
    "children" | "style"
  >;
  "data-psone-progress-card"?: string;
};

/** Shared flat action-card shell for pre-session baseline steps. */
export default function BaselineActionCard({
  eyebrow,
  label,
  description,
  metaLabel,
  icon,
  overlay,
  iconStyle,
  metaStyle,
  descriptionStyle,
  metaButtonProps,
  "data-psone-progress-card": dataPsoneProgressCard,
  className,
  style,
  type,
  ...props
}: BaselineActionCardProps) {
  const rootClassName = cn(
    "baseline-action-card relative inline-flex min-h-[5.25rem] w-full min-w-0 items-center justify-start overflow-hidden rounded-[var(--radius-lg)] border px-4 py-4 text-left transition-[background-color,border-color,color] duration-150 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[color:color-mix(in_srgb,var(--color-primary)_24%,transparent)] disabled:cursor-not-allowed disabled:opacity-70 md:min-h-[5.5rem]",
    className,
  );
  const metaButtonClassName = metaButtonProps?.className;
  const metaButtonType = metaButtonProps?.type;
  const metaButtonRest = metaButtonProps
    ? { ...metaButtonProps, className: undefined, type: undefined }
    : null;

  const content = (
    <>
      {overlay}

      <span className="relative z-[1] flex w-full min-w-0 items-center gap-3">
        <span
          className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-[var(--radius-md)] border"
          style={iconStyle}
        >
          {icon}
        </span>

        <span className="min-w-0 flex-1">
          <span className="baseline-action-card-eyebrow block text-[0.64rem] font-extrabold uppercase tracking-[0.16em] text-inherit 2xl:text-[0.6875rem]">
            {eyebrow}
          </span>
          <span className="baseline-action-card-label mt-1 block text-[0.98rem] font-[650] leading-[1.15] text-inherit 2xl:text-[1.05rem]">
            {label}
          </span>
          <span
            className="baseline-action-card-description mt-1 block text-[0.74rem] leading-[1.4] 2xl:text-[0.8rem]"
            style={descriptionStyle}
          >
            {description}
          </span>
        </span>

        {metaButtonRest ? (
          <button
            {...metaButtonRest}
            type={metaButtonType ?? "button"}
            className={cn(
              "baseline-action-card-meta theme-button-primary inline-flex shrink-0 rounded-full border px-3 py-1 text-[0.68rem] font-bold uppercase tracking-[0.12em] 2xl:text-[0.72rem]",
              metaButtonClassName,
            )}
            style={metaStyle}
          >
            {metaLabel}
          </button>
        ) : (
          <span
            className="baseline-action-card-meta inline-flex shrink-0 rounded-full border px-3 py-1 text-[0.68rem] font-bold uppercase tracking-[0.12em] 2xl:text-[0.72rem]"
            style={metaStyle}
          >
            {metaLabel}
          </span>
        )}
      </span>
    </>
  );

  if (metaButtonProps) {
    return (
      <div
        className={rootClassName}
        style={style}
        data-psone-progress-card={dataPsoneProgressCard}
      >
        {content}
      </div>
    );
  }

  return (
    <button
      type={type ?? "button"}
      className={rootClassName}
      style={style}
      data-psone-progress-card={dataPsoneProgressCard}
      {...props}
    >
      {content}
    </button>
  );
}
